/**
 * Vitaly Cart Fix — Shrine PRO
 * Registreert alle ontbrekende custom elements +
 * quantity update met price refresh + header cart link
 */

/* ─── HELPERS ─── */
function debounce(fn, wait = 300) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getCartSectionId() {
  const sectionEl = document.getElementById('shopify-section-cart-drawer');
  return sectionEl?.dataset?.sectionId || 'cart-drawer';
}

function updateCartCount(count) {
  document.querySelectorAll('.cart-count-bubble').forEach(el => {
    const hidden = el.querySelector('span[aria-hidden]');
    const visible = el.querySelector('span:not([aria-hidden])');
    if (hidden) hidden.textContent = count;
    if (visible) visible.textContent = count;
    el.classList.toggle('hidden', count === 0);
  });
}

function renderDrawer(sectionsHtml, sectionId) {
  const parsed = new DOMParser().parseFromString(sectionsHtml, 'text/html');
  const newInner = parsed.querySelector('.cart-drawer');
  const currentInner = document.getElementById('CartDrawer');
  if (newInner && currentInner) {
    currentInner.innerHTML = newInner.innerHTML;
  }
}

/* ─── CART DRAWER ─── */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('#CartDrawer-Overlay');
      this.overlay?.addEventListener('click', this.close.bind(this));
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape' && this.classList.contains('active')) this.close();
      });
    }
    open(triggeredBy) {
      if (triggeredBy) this.activeElement = triggeredBy;
      this.setAttribute('aria-expanded', 'true');
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
    }
    close() {
      this.setAttribute('aria-expanded', 'false');
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      this.activeElement?.focus?.();
    }
    renderContents(parsedState) {
      const sectionId = getCartSectionId();
      if (parsedState.sections?.[sectionId]) {
        renderDrawer(parsedState.sections[sectionId], sectionId);
      }
      updateCartCount(parsedState.item_count);
      this.open();
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}

/* ─── CART DRAWER ITEMS ─── */
if (!customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends HTMLElement {}
  customElements.define('cart-drawer-items', CartDrawerItems);
}

/* ─── PRODUCT FORM ─── */
if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitBtn = this.querySelector('[type="submit"]');
      this.form?.addEventListener('submit', this.onSubmit.bind(this));
    }
    onSubmit(e) {
      e.preventDefault();
      if (this.submitBtn) {
        this.submitBtn.setAttribute('disabled', '');
        this.submitBtn.classList.add('loading');
      }
      const formData = new FormData(this.form);
      const sectionId = getCartSectionId();
      const body = JSON.stringify({
        ...Object.fromEntries(formData.entries()),
        sections: sectionId,
        sections_url: window.location.pathname,
      });
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body,
      })
        .then(r => r.json())
        .then(data => {
          if (data.status) {
            this.handleErrorMessage(data.description);
            return;
          }
          const drawer = document.querySelector('cart-drawer');
          if (data.sections && drawer) {
            drawer.renderContents(data);
          } else {
            fetch('/cart.js').then(r => r.json()).then(cart => {
              drawer?.updateCount?.(cart.item_count);
              drawer?.open?.(this.submitBtn);
            });
          }
        })
        .catch(e => console.error('[VitalyCart] ATC error:', e))
        .finally(() => {
          if (this.submitBtn) {
            this.submitBtn.removeAttribute('disabled');
            this.submitBtn.classList.remove('loading');
          }
        });
    }
    handleErrorMessage(msg) {
      let errEl = this.querySelector('.product-form__error-message-wrapper');
      if (!errEl) {
        errEl = document.createElement('p');
        errEl.className = 'product-form__error-message-wrapper';
        errEl.style.cssText = 'color:red;margin-top:8px;font-size:14px;';
        this.form?.appendChild(errEl);
      }
      errEl.textContent = msg || 'Er is iets misgegaan. Probeer opnieuw.';
    }
  }
  customElements.define('product-form', ProductForm);
}

/* ─── CART REMOVE BUTTON ─── */
if (!customElements.get('cart-remove-button')) {
  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.querySelector('a, button')?.addEventListener('click', (e) => {
        e.preventDefault();
        const line = this.dataset.index;
        if (!line) return;
        const sectionId = getCartSectionId();
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line), quantity: 0, sections: sectionId })
        })
        .then(r => r.json())
        .then(state => {
          if (state.sections?.[sectionId]) renderDrawer(state.sections[sectionId], sectionId);
          updateCartCount(state.item_count);
          if (state.item_count === 0) document.querySelector('cart-drawer')?.classList.add('is-empty');
        });
      });
    }
  }
  customElements.define('cart-remove-button', CartRemoveButton);
}

/* ─── QUANTITY INPUT ─── */
if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });
      this.querySelector('.quantity__button[name="plus"]')?.addEventListener('click', () => {
        this.input.stepUp();
        this.input.dispatchEvent(this.changeEvent);
      });
      this.querySelector('.quantity__button[name="minus"]')?.addEventListener('click', () => {
        this.input.stepDown();
        this.input.dispatchEvent(this.changeEvent);
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);
}

/* ─── QUANTITY CHANGE → PRIJS UPDATE (event delegation) ─── */
document.addEventListener('change', debounce(function (e) {
  const input = e.target;
  // Alleen inputs binnen cart-drawer
  if (!input.closest('cart-drawer')) return;
  if (input.type !== 'number' && !input.classList.contains('quantity__input')) return;

  const line = input.dataset.index || input.dataset.line;
  const quantity = parseInt(input.value);
  if (!line) return;

  // Disable alle quantity inputs
  document.querySelectorAll('cart-drawer input[type="number"], cart-drawer .quantity__input')
    .forEach(i => i.setAttribute('disabled', ''));

  const sectionId = getCartSectionId();

  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      line: parseInt(line),
      quantity: quantity,
      sections: sectionId,
      sections_url: window.location.pathname
    })
  })
  .then(r => r.json())
  .then(state => {
    if (state.sections?.[sectionId]) renderDrawer(state.sections[sectionId], sectionId);
    updateCartCount(state.item_count);
    if (state.item_count === 0) document.querySelector('cart-drawer')?.classList.add('is-empty');
  })
  .catch(err => console.error('[VitalyCart] quantity error:', err));
}, 300));

/* ─── HEADER CART LINK → OPEN DRAWER ─── */
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('a[href="/cart"]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        e.preventDefault();
        drawer.open(link);
      }
    });
  });
});

console.log('[VitalyCart] ✅ Geladen');

/* ─── COUNTDOWN TIMER ─── */
if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const endTime = this.dataset.end || this.dataset.date || this.getAttribute('data-end') || this.getAttribute('data-date');
      const daysEl    = this.querySelector('[data-days], .timer__days, .countdown__days');
      const hoursEl   = this.querySelector('[data-hours], .timer__hours, .countdown__hours');
      const minsEl    = this.querySelector('[data-minutes], .timer__minutes, .countdown__minutes');
      const secsEl    = this.querySelector('[data-seconds], .timer__seconds, .countdown__seconds');

      if (!secsEl && !minsEl) return; // geen timer elementen gevonden

      const target = endTime ? new Date(endTime).getTime() : Date.now() + (24 * 60 * 60 * 1000);

      const tick = () => {
        const now  = Date.now();
        const diff = Math.max(0, target - now);

        const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours   = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        const pad = n => String(n).padStart(2, '0');

        if (daysEl)  daysEl.textContent  = pad(days);
        if (hoursEl) hoursEl.textContent = pad(hours);
        if (minsEl)  minsEl.textContent  = pad(minutes);
        if (secsEl)  secsEl.textContent  = pad(seconds);

        if (diff > 0) requestAnimationFrame(tick);
      };

      tick();
    }
  }
  customElements.define('countdown-timer', CountdownTimer);
}